import React from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import SentimentDissatisfied from '@mui/icons-material/SentimentDissatisfied';
import Block from '@mui/icons-material/Block';
import CloudOff from '@mui/icons-material/CloudOff';
import Lock from '@mui/icons-material/Lock';
import ErrorOutline from '@mui/icons-material/ErrorOutline';

const ERROR_TEMPLATES = {
  '404': {
    title: 'Page Not Found',
    description: "The page you're looking for doesn't exist.",
    icon: SentimentDissatisfied,
    iconBgColor: '#eff6ff',
    iconColor: '#3b82f6',
    actionLabel: 'Back to Home',
    actionType: 'navigate',
  },
  '403': {
    title: 'Access Forbidden',
    description: "You don't have permission to access this resource.",
    icon: Block,
    iconBgColor: '#fef2f2',
    iconColor: '#dc2626',
    actionLabel: 'Back to Home',
    actionType: 'navigate',
  },
  '500': {
    title: 'Server Error',
    description: 'Something went wrong on our end.',
    icon: ErrorOutline,
    iconBgColor: '#fef2f2',
    iconColor: '#ef4444',
    actionLabel: 'Retry',
    actionType: 'retry',
  },
  network: {
    title: 'Connection Error',
    description: 'Unable to reach the server.',
    icon: CloudOff,
    iconBgColor: '#f0fdf4',
    iconColor: '#059669',
    actionLabel: 'Retry',
    actionType: 'retry',
  },
  permission: {
    title: 'Permission Denied',
    description: "Your account doesn't have the required permissions.",
    icon: Lock,
    iconBgColor: '#fffbeb',
    iconColor: '#d97706',
    actionLabel: 'Back to Home',
    actionType: 'navigate',
  },
};

/**
 * ErrorState — Full-page error templates for common error scenarios.
 *
 * Props:
 *   type: '404'|'403'|'500'|'network'|'permission' — determines the template shown
 *   message: string — optional custom description to override the template default
 *   onRetry: function — callback for retry action (required for network/500 types)
 *   supportUrl: string — URL for the "Contact Support" link (default: '/support')
 *
 * Each template includes:
 *   - Large icon with colored background circle (80px)
 *   - Title (h5 typography)
 *   - Description (body2)
 *   - Primary action button
 *   - Secondary "Contact Support" link
 *
 * Layout: centered column, py=10
 */
export default function ErrorState({ type = '500', message, onRetry, supportUrl = '/support' }) {
  const navigate = useNavigate();
  const template = ERROR_TEMPLATES[type] || ERROR_TEMPLATES['500'];
  const IconComponent = template.icon;

  const handlePrimaryAction = () => {
    if (template.actionType === 'retry' && onRetry) {
      onRetry();
    } else {
      navigate('/');
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 10,
        textAlign: 'center',
      }}
    >
      {/* Icon with colored background circle */}
      <Box
        sx={{
          width: 120,
          height: 120,
          borderRadius: '50%',
          backgroundColor: template.iconBgColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 3,
        }}
      >
        <IconComponent sx={{ fontSize: 80, color: template.iconColor }} />
      </Box>

      {/* Title */}
      <Typography variant="h5" fontWeight={700} gutterBottom>
        {template.title}
      </Typography>

      {/* Description */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400 }}>
        {message || template.description}
      </Typography>

      {/* Primary action button */}
      <Button
        variant="contained"
        size="large"
        onClick={handlePrimaryAction}
        sx={{ mb: 2 }}
      >
        {template.actionLabel}
      </Button>

      {/* Secondary support link */}
      <Link
        href={supportUrl}
        underline="hover"
        color="text.secondary"
        variant="body2"
      >
        Contact Support
      </Link>
    </Box>
  );
}
